import { useState } from 'react';
import { useDarkMode } from '../context/DarkModeContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router';
import { Mail, RefreshCw, CheckCircle, ArrowLeft } from 'lucide-react';

export default function VerifyPrompt() {
  const { darkMode } = useDarkMode();
  const { user, resendVerification, logout } = useAuth();
  const navigate = useNavigate();
  const [resent, setResent] = useState(false);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);

  const handleResend = async () => {
    setSending(true);
    setError('');
    try {
      await resendVerification();
      setResent(true);
      setTimeout(() => setResent(false), 5000);
    } catch (err: any) {
      if (err.message?.includes('429') || err.status === 429) {
        setError('Bitte warten Sie einen Moment, bevor Sie es erneut versuchen.');
      } else if (err.message?.includes('401') || err.message?.includes('Unauthorized') || err.status === 401) {
        setError('Sitzung abgelaufen. Bitte melden Sie sich erneut an.');
      } else {
        setError(err.message || 'Fehler beim erneuten Senden');
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className={`min-h-screen flex items-center justify-center px-4 py-12 ${
        darkMode ? 'bg-slate-900' : 'bg-slate-50'
      }`}
    >
      <div
        className={`w-full max-w-md rounded-2xl border shadow-lg p-8 text-center ${
          darkMode
            ? 'bg-slate-800 border-slate-700'
            : 'bg-white border-slate-200'
        }`}
      >
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
            <Mail className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <h1
          className={`text-2xl font-bold mb-2 ${
            darkMode ? 'text-white' : 'text-slate-900'
          }`}
        >
          E-Mail bestätigen
        </h1>

        <p
          className={`mb-6 ${
            darkMode ? 'text-slate-400' : 'text-slate-500'
          }`}
        >
          Wir haben einen Bestätigungslink an{' '}
          <span className="font-medium text-blue-600">{user?.email}</span>{' '}
          gesendet. Bitte prüfen Sie Ihr Postfach und klicken Sie auf den Link.
        </p>

        {resent && (
          <div className="mb-4 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 text-sm flex items-center justify-center gap-2">
            <CheckCircle className="w-4 h-4" />
            Neuer Verifizierungslink wurde gesendet
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={handleResend}
            disabled={sending}
            className="w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <span className="inline-flex">
              <RefreshCw className={`w-4 h-4 ${sending ? 'animate-spin' : ''}`} />
            </span>
            <span>{sending ? 'Wird gesendet...' : 'Erneut senden'}</span>
          </button>

          <button
            onClick={() => { logout(); navigate('/register'); }}
            className={`w-full py-2.5 px-4 border rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
              darkMode
                ? 'border-slate-600 text-slate-300 hover:bg-slate-700'
                : 'border-slate-300 text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Registrierung
          </button>
        </div>

        <p
          className={`mt-6 text-xs ${
            darkMode ? 'text-slate-500' : 'text-slate-400'
          }`}
        >
          Prüfen Sie auch Ihren Spam-Ordner. Der Link ist 24 Stunden gültig.
        </p>
      </div>
    </div>
  );
}
